import { formatDataBr } from "@/modules/recursos";
import { hojeBrasilia } from "@/modules/obras/hoje-brasilia";
import { useMemo, useState } from "react";
import type {
  SelectButtonChangeEvent,
  SelectButtonPassThroughOptions,
} from "primereact/selectbutton";
import { useObraEmbarcacaoInformacoes, useObraPeriodos } from "@/modules/obras";
import {
  calcularCronograma,
  intervaloPeriodoObra,
  readDuracao,
} from "../../../../../hooks/nova-obra.duracao";
import { useNovaObra } from "../../../../../hooks/nova-obra.context";
import { REVISAO, rotuloPeriodo } from "../../../../../nova-obra.constants";
import { montarGantt } from "../../../revisao-gantt/revisao-gantt.model";
import type { VisaoTimeline, LinhaTempo } from "./types-linha-do-tempo";

interface OpcaoVisao {
  readonly value: VisaoTimeline;
  readonly label: string;
}

/** Alternador Tabela/Gantt (Figma `alternador visão`, `SelectButton` do tema). */
const OPCOES_VISAO: OpcaoVisao[] = [
  { value: "tabela", label: REVISAO.duracao.tabela },
  { value: "gantt", label: REVISAO.duracao.gantt },
];

/** `data-testid` de cada opção (`revisao-visao-tabela`/`-gantt`), pelo `pt` do Prime. */
const PT_VISAO: SelectButtonPassThroughOptions = {
  button: (opcoes) => ({
    "data-testid": `revisao-visao-${(opcoes?.context.option as OpcaoVisao | undefined)?.value}`,
  }),
};

export function useLinhaDoTempo() {
  const { state } = useNovaObra();
  const periodos = useObraPeriodos();
  const [visao, setVisao] = useState<VisaoTimeline>("tabela");
  const informacoes = useObraEmbarcacaoInformacoes(state.obra.embarcacaoId);
  const dados = useMemo(() => readDuracao(state.etapas.duracao), [state.etapas.duracao]);
  const cronograma = useMemo(() => calcularCronograma(dados), [dados]);
  const linhas = useMemo<LinhaTempo[]>(
    () =>
      dados.periodos.map((periodo, indice) => {
        const tipo = periodos.data.find((item) => item.id === periodo.periodoObraId);
        const calculado = cronograma.linhas[indice];
        return {
          uid: periodo.uid,
          // HU 2523 RN04: período de Obra aparece como "Obra – Fundeio" (prevalece sobre a tag do Figma).
          rotulo: tipo ? rotuloPeriodo(tipo.descricao, tipo.etapa) : REVISAO.vazio,
          etapa: tipo?.etapa ?? null,
          inicio: calculado?.inicio ?? null,
          fim: calculado?.fim ?? null,
          duracao: periodo.duracao,
          meta: periodo.meta,
        };
      }),
    [dados, cronograma, periodos.data],
  );
  const hoje = useMemo(() => hojeBrasilia(), []);
  const gantt = useMemo(
    () =>
      montarGantt({
        periodos: linhas,
        total:
          cronograma.inicio && cronograma.terminoPrevisto
            ? { inicio: cronograma.inicio, fim: cronograma.terminoPrevisto }
            : null,
        obra: intervaloPeriodoObra(dados, cronograma, periodos.data),
        limites:
          informacoes.status === "success" && informacoes.data
            ? informacoes.data.limitesClasse
            : [],
        hoje,
      }),
    [linhas, dados, cronograma, periodos.data, informacoes.status, informacoes.data, hoje],
  );

  // `allowEmpty={false}` já impede o clique na opção ativa de limpar a escolha; o `?? visao` cobre o tipo.
  const mudarVisao = (evento: SelectButtonChangeEvent) =>
    setVisao((evento.value as VisaoTimeline | null) ?? visao);

  return {
    visao,
    mudarVisao,
    opcoesVisao: OPCOES_VISAO,
    ptVisao: PT_VISAO,
    linhas: linhas.map((linha) => ({
      ...linha,
      inicioFormatado: formatDataBr(linha.inicio ?? ""),
      fimFormatado: formatDataBr(linha.fim ?? ""),
    })),
    gantt,
    temGantt: visao === "gantt",
  };
}
